import type { EnvValueType } from '@hangar/shared';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { convertLiteralNewlines, fileToBase64 } from './envHelpers';
import { base64Bytes, literalCountFor, pluralS, realLineCount, valueFieldError, valueHint, valueRows } from './valueModel';

export interface ValueState {
  value: string;
  /** Set when a file was picked (`valueBase64`); `value` then holds the file name for display. */
  base64?: string;
  fileName?: string;
}

interface Props {
  id: string;
  valueType: EnvValueType;
  state: ValueState;
  onChange: (next: ValueState) => void;
  onValueTypeChange?: (t: EnvValueType) => void;
  error?: string;
  required?: boolean;
  submitted?: boolean;
}

/**
 * Value input with the literal-`\n` UX (SPEC §12.1, the Scanbot license bug): a two-character `\n` in a non-multiline
 * value is flagged and can be converted to real line breaks in one click; `json` is validated live; `file` uploads
 * as base64. Values only ever travel to the API — they are never echoed back.
 */
export function ValueEditor({ id, valueType, state, onChange, onValueTypeChange, error, required, submitted }: Props) {
  const localError = valueFieldError({ error, required, submitted, valueType, state });
  if (valueType === 'file') return <FileValueField id={id} required={required} error={localError} state={state} onChange={onChange} />;
  return (
    <FormField id={id} label="Value" required={required} error={localError} hint={valueHint(valueType, realLineCount(state.value))}>
      <Textarea
        data-testid={id}
        value={state.value}
        onChange={(e) => onChange({ value: e.target.value })}
        rows={valueRows(valueType)}
        spellCheck={false}
        autoComplete="off"
        className="font-mono sm:text-[13px]"
        {...fieldA11y(id, localError)}
      />
      <LiteralNewlineNotice valueType={valueType} state={state} onChange={onChange} onValueTypeChange={onValueTypeChange} />
    </FormField>
  );
}

interface FileProps {
  id: string;
  required?: boolean;
  error?: string;
  state: ValueState;
  onChange: (next: ValueState) => void;
}

function pickFile(file: File | undefined, onChange: (next: ValueState) => void) {
  if (!file) return onChange({ value: '' });
  void fileToBase64(file).then((b64) => onChange({ value: file.name, base64: b64, fileName: file.name }));
}

function FileValueField({ id, required, error, state, onChange }: FileProps) {
  return (
    <FormField id={id} label="File" required={required} error={error} hint="Uploaded byte-exact as base64 (≤ 64 KiB) and materialized at build time">
      <Input type="file" data-testid={id} onChange={(e) => pickFile(e.target.files?.[0], onChange)} {...fieldA11y(id, error)} />
      {state.fileName ? (
        <p className="font-mono text-xs text-muted-foreground">
          {state.fileName} · {base64Bytes(state.base64)} B
        </p>
      ) : null}
    </FormField>
  );
}

interface NoticeProps {
  valueType: EnvValueType;
  state: ValueState;
  onChange: (next: ValueState) => void;
  onValueTypeChange?: (t: EnvValueType) => void;
}

function LiteralNewlineNotice({ valueType, state, onChange, onValueTypeChange }: NoticeProps) {
  const literal = literalCountFor(valueType, state.value);
  if (literal === 0) return null;
  const breaks = realLineCount(state.value) - 1;
  const convert = () => {
    onChange({ value: convertLiteralNewlines(state.value) });
    if (valueType !== 'multiline') onValueTypeChange?.('multiline');
  };
  return (
    <div data-testid="env-literal-newline" className="flex flex-wrap items-center gap-2 rounded-md border border-awaiting/30 bg-awaiting/10 px-2.5 py-1.5 text-[13px] text-awaiting">
      <Icon name="warning" size={16} />
      <span className="min-w-0 flex-1">
        <code className="mr-1 font-mono text-[11px]">LITERAL_BACKSLASH_N</code>
        {literal} literal <code className="font-mono">\n</code> sequence{pluralS(literal)} and {breaks} real line break{pluralS(breaks)} — Scanbot-style licenses need real newlines.
      </span>
      <Button type="button" size="sm" variant="outline" data-testid="env-convert-newlines" onClick={convert}>
        Convert to real line breaks
      </Button>
    </div>
  );
}
